import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuthStore } from "../store/authStore";
import SEO from "./SEO";

export default function AdminAuthors() {
  const [authors, setAuthors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingAuthor, setEditingAuthor] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    bio: "",
    image_url: "",
  });

  const { user } = useAuthStore();

  useEffect(() => {
    fetchAuthors();
  }, []);

  async function fetchAuthors() {
    try {
      setLoading(true);
      console.log("Fetching authors...");

      const { data, error } = await supabase
        .from("blog_authors")
        .select("*")
        .order("name");

      if (error) {
        console.error("Error fetching authors:", error);
        throw error;
      }

      console.log("Authors data:", data);
      setAuthors(data || []);
    } catch (err) {
      console.error("Error fetching authors:", err);
      setError("Failed to load authors");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      if (editingAuthor) {
        const { error } = await supabase
          .from("blog_authors")
          .update(formData)
          .eq("id", editingAuthor.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("blog_authors")
          .insert([formData]);

        if (error) throw error;
      }

      setFormData({ name: "", slug: "", bio: "", image_url: "" });
      setEditingAuthor(null);
      fetchAuthors();
    } catch (err) {
      console.error("Error saving author:", err);
      alert("Failed to save author");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Are you sure you want to delete this author?")) return;

    try {
      const { error } = await supabase
        .from("blog_authors")
        .delete()
        .eq("id", id);

      if (error) throw error;
      fetchAuthors();
    } catch (err) {
      console.error("Error deleting author:", err);
      alert("Failed to delete author");
    }
  }

  function handleEdit(author: any) {
    setEditingAuthor(author);
    setFormData({
      name: author.name,
      slug: author.slug,
      bio: author.bio || "",
      image_url: author.image_url || "",
    });
  }

  if (!user) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
        <p className="text-yellow-700">Please log in to manage authors.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="One80Labs - Manage Authors"
        description="Admin interface for managing blog authors in the One80Labs content management system."
        noindex={true}
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Authors</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingAuthor ? "Edit Author" : "Add New Author"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="slug"
                  className="block text-sm font-medium text-gray-700"
                >
                  Slug
                </label>
                <input
                  type="text"
                  id="slug"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="bio"
                  className="block text-sm font-medium text-gray-700"
                >
                  Bio
                </label>
                <textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) =>
                    setFormData({ ...formData, bio: e.target.value })
                  }
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                />
              </div>

              <div>
                <label
                  htmlFor="image_url"
                  className="block text-sm font-medium text-gray-700"
                >
                  Profile Image URL
                </label>
                <input
                  type="url"
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, image_url: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                />
              </div>

              <div className="flex justify-end space-x-3">
                {editingAuthor && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingAuthor(null);
                      setFormData({
                        name: "",
                        slug: "",
                        bio: "",
                        image_url: "",
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  {editingAuthor ? "Update Author" : "Add Author"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Author List
            </h3>
            <div className="space-y-4">
              {authors.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No authors found
                </p>
              ) : (
                authors.map((author) => (
                  <div
                    key={author.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-md"
                  >
                    <div className="flex items-center space-x-4">
                      {author.image_url && (
                        <img
                          src={author.image_url}
                          alt={author.name}
                          className="h-10 w-10 rounded-full"
                        />
                      )}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {author.name}
                        </h4>
                        {author.bio && (
                          <p className="text-sm text-gray-500 line-clamp-1">
                            {author.bio}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(author)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(author.id)}
                        className="text-red-400 hover:text-red-500"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
